<template>
    <ModalWrapper v-model="modal" max-width="700">
        <template v-slot:title>
            {{ $t('edit_clients_profiles') }}
        </template>
        <template v-slot:content>
            <v-form v-model="valid" :disabled="loading" data-cy="edit-clients-form">
                <v-row dense>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            v-model="formData.managerId"
                            item-title="name"
                            item-value="personId"
                            :items="users"
                            :label="$t('manager')"
                            :loading="usersLoading"
                            :disabled="usersLoading"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="manager-id"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-autocomplete
                            v-model="formData.countryId"
                            :items="countries"
                            :label="$t('country')"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="country-id"
                        />
                    </v-col>
                    <v-col v-if="isShowField" cols="12" sm="6">
                        <v-select
                            v-model="formData.stage"
                            :items="stages"
                            :label="$t('stage')"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="stage"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" :order="!isShowField ? 3 : ''" :order-sm="!isShowField ? 4 : ''">
                        <v-autocomplete
                            v-model="formData.cityId"
                            :items="cities"
                            :label="$t('city')"
                            :disabled="isCitiesLoading || !cities.length"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="city-id"
                        />
                    </v-col>
                    <v-col v-if="isShowField" cols="12" sm="6">
                        <v-select
                            v-model="formData.priority"
                            :items="priority"
                            :label="$t('priority')"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="priority"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" :order="!isShowField ? 4 : ''" :order-sm="!isShowField ? 3 : ''">
                        <v-select
                            v-model="formData.language"
                            :items="locales"
                            :label="$t('language')"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="language"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </template>
        <template v-slot:btns>
            <v-btn variant="flat" color="primary" :disabled="loading" :loading="loading" data-cy="save" @click="send">
                {{ $t('save') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Watch, Vue, VModel} from 'vue-facing-decorator'
    import {clean, clone} from '@/utils/helpers'
    import {Priority, Stage, type SelectOptionType} from '~/utils/types/types'

    const initFormData = () => ({
        managerId: null,
        countryId: null,
        cityId: null,
        stage: null,
        priority: null,
        language: null,
    })

    @Component({components: {}})
    export class EditClientsModal extends Vue {
        @VModel() modal
        @Prop({default: [], required: true}) clients: number[]
        @Prop({default: [], required: true}) users
        @Prop({default: false, required: true}) usersLoading: boolean
        @Emit()
        sent() {}
        @Emit()
        close() {}
        @Watch('formData.countryId')
        async onLocaleChange() {
            if (this.formData.countryId) {
                this.formData.cityId = null
                try {
                    this.isCitiesLoading = true
                    const rs = await this.$api.locations.get({
                        limitCities: -1,
                        orderBy: 'ALPHABET',
                        countryId: this.formData.countryId,
                    })
                    if (rs.cities) {
                        this.cities = rs.cities.map(({id, name}) => {
                            return {title: name, value: id}
                        })
                    }
                } catch (e) {
                    this.cities = []
                } finally {
                    this.isCitiesLoading = false
                }
            } else {
                this.formData.cityId = null
                this.cities = []
            }
        }
        @Watch('modal')
        async onShowModal() {
            if (this.modal) {
                this.formData = initFormData()
            }
        }

        formData = initFormData()
        valid = false
        loading = false
        cities: SelectOptionType[] = []
        isCitiesLoading: boolean = true
        dialog = false

        async send() {
            try {
                this.loading = true
                const formData = clone(this.formData)
                clean(formData)

                await this.$api.privateClientsMassEdit.post({
                    ids: this.clients,
                    ...formData,
                })

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.sent()
            } catch (e) {
                this.$toast.error(this.$t('data_update_fail'))
            } finally {
                this.loading = false
            }
        }

        get locales(): SelectOptionType[] {
            return Object.values(this.$conf.public.locales).map(lang => {
                return {value: lang, title: this.$t(`languages.${lang}`)}
            })
        }

        get countries(): SelectOptionType[] {
            return runtimeStore.countries.map(({id, name}) => {
                return {title: name, value: id}
            })
        }

        get priority(): SelectOptionType[] {
            const priorities: Priority[] = [Priority.VIP, Priority.High, Priority.Normal, Priority.Low]
            return priorities.map(value => ({value, title: this.$t(`crmPriority.${value}`)}))
        }

        get stages(): SelectOptionType[] {
            const stages: Stage[] = [Stage.Client, Stage.Potential]
            return stages.map(value => ({value, title: this.$t(`crmStage.${value}`)}))
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isCRMExtendedModule() {
            return runtimeStore.CRMSettings?.extendedCrm || false
        }

        get isShowField() {
            return !this.isAgency && !this.isTO2 && this.isCRMExtendedModule
        }
    }

    export default EditClientsModal
</script>
